/**
 * f16.1: the current cycle's figures and the "Commitment by Business Unit
 * Group" chart. Every amount is f12.1's own total (BR-F16.1-4); this card
 * formats, it never computes. Absence renders as a sentence, never `$0`.
 */

import type { DashboardFigures, DashboardScoped } from '@/api/client';
import { CycleStateBadge } from '@/routes/cycles/CycleStateBadge';
import { deltaClass, formatDelta, formatMoney, type MoneyLike } from '@/routes/cycles/review/reviewFormat';
import { CommitmentByGroupChart } from '@/ui/charts/CommitmentByGroupChart';
import { useChartPalette } from '@/ui/charts/useChartPalette';

import { formatPercent, shortComparison } from './dashboardView';

const NO_USD_RATE = 'No USD rate for this cycle';

function Amount({ money, fallback = '—' }: { money: MoneyLike | null | undefined; fallback?: string }) {
  if (!money) return <div className="stat-value sm">{fallback}</div>;
  return (
    <div className="stat-value sm num" title={`${money.amount} ${money.currency_code}`}>
      {formatMoney(money)}
    </div>
  );
}

function Figure({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      {children}
    </div>
  );
}

function FiguresGrid({ body, figures }: { body: DashboardScoped; figures: DashboardFigures }) {
  const cycle = body.cycle!;
  const missingRate = figures.conversion.error !== null;
  const converting = figures.conversion.pending ? ' · Converting…' : '';
  const comparison = shortComparison(cycle.comparison_label);
  const gap = formatPercent(figures.budget_gap_pct);
  const [foreign] = figures.foreign_units;
  const columns = foreign ? 'grid-4' : 'grid-3';

  return (
    <div className={`grid ${columns} dash-figures`}>
      <Figure label={`FY ${cycle.period_year} commitment`}>
        <Amount money={figures.current_com} fallback={missingRate ? NO_USD_RATE : '—'} />
        <div className="stat-foot">
          {figures.delta_to_last_com && comparison ? (
            <>
              <span className={deltaClass(figures.delta_to_last_com)}>{formatDelta(figures.delta_to_last_com)}</span>
              {` vs ${comparison}${converting}`}
            </>
          ) : (
            `No frozen baseline yet${converting}`
          )}
        </div>
      </Figure>
      <Figure label={`Budget ${cycle.period_year}`}>
        {body.capabilities.view_budget ? (
          <>
            <Amount money={figures.budget} />
            {gap && (
              <div className="stat-foot">
                <span className={deltaClass({ amount: figures.budget_gap_pct ?? '0', currency_code: '' })}>{gap}</span>
                gap to commitment
              </div>
            )}
          </>
        ) : (
          <>
            <div className="stat-value sm" title="Finance only">—</div>
            <div className="stat-foot">Finance only</div>
          </>
        )}
      </Figure>
      <Figure label="Unweighted">
        <Amount money={figures.unweighted} fallback={missingRate ? NO_USD_RATE : '—'} />
        <div className="stat-foot">
          {`P2 ${formatMoney(figures.p2)} · P3 ${formatMoney(figures.p3)}`}
        </div>
      </Figure>
      {foreign && (
        <Figure label={foreign.name}>
          <Amount money={foreign.current_com} />
          <div className="stat-foot">
            {foreign.current_com_presented
              ? `${formatMoney(foreign.current_com_presented)}${foreign.rate ? ` at ${foreign.rate}` : ''}`
              : NO_USD_RATE}
          </div>
        </Figure>
      )}
    </div>
  );
}

function Unavailable({ body }: { body: DashboardScoped }) {
  const text =
    body.figures_unavailable_reason === 'NO_VISIBLE_UNITS'
      ? 'None of your Business Units is in this cycle yet.'
      : `No forecast lines yet for ${body.cycle?.code ?? 'this cycle'}.`;
  return <p className="dash-empty">{text}</p>;
}

export function CycleCard({ body }: { body: DashboardScoped }) {
  const colors = useChartPalette();
  const cycle = body.cycle;

  if (!cycle) {
    return (
      <div className="card">
        <div className="card-head"><h3>No cycle yet</h3></div>
        <div className="card-body">
          <p className="dash-empty">
            No commitment cycle is open to you yet. Figures appear here once Finance releases one.
          </p>
        </div>
      </div>
    );
  }

  const figures = body.figures;
  const groups = (body.groups ?? []).flatMap((group) =>
    group.current_com_presented ? [{ id: group.id, name: group.name, money: group.current_com_presented }] : [],
  );

  return (
    <div className="card">
      <div className="card-head">
        <h3>{`Cycle ${cycle.code} · ${cycle.label}`}</h3>
        <CycleStateBadge state={cycle.state} />
      </div>
      <div className="card-body">
        {figures ? (
          <>
            <FiguresGrid body={body} figures={figures} />
            <div className="dash-rate-label">
              {figures.pending_lines > 0 && (
                <span className="badge pill-pending">{`Recalculating… (${figures.pending_lines} lines)`}</span>
              )}
              <span>
                {figures.conversion.rate_label ?? `in ${figures.presentation_currency}`}
              </span>
            </div>

            <div className="divider" />
            <div className="eyebrow">Commitment by Business Unit Group</div>
            {figures.conversion.error ? (
              <p className="dash-empty">{NO_USD_RATE}</p>
            ) : groups.length > 0 ? (
              <CommitmentByGroupChart groups={groups} currency={figures.presentation_currency} colors={colors} />
            ) : (
              <p className="dash-empty">No commitment recorded yet.</p>
            )}
          </>
        ) : (
          <Unavailable body={body} />
        )}
      </div>
    </div>
  );
}
